import { useEffect, useState } from 'react'
import { useMediaQuery } from './useMediaQuery'

const ROTATION_INTERVAL_MS = 2000

/**
 * Drives the mobile trust-signals carousel.
 *
 * - Advances `activeIndex` every 2s through `[0, slotCount)`.
 * - Pauses while the tab is hidden (`document.visibilityState === 'hidden'`)
 *   and resumes on return — interval is restarted, not silently ticking.
 * - Reports `prefers-reduced-motion` so the consumer can drop the CSS fade.
 *   Rotation itself still runs; only the transition duration is dropped at
 *   the style layer.
 * - If `slotCount <= 1`, no interval is registered.
 *
 * Returned `activeIndex` is always clamped to `[0, slotCount)`, even if
 * `slotCount` shrinks mid-rotation.
 */
export function useBadgeRotation (slotCount: number): { activeIndex: number, reducedMotion: boolean } {
  const [rawIndex, setRawIndex] = useState(0)
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  useEffect(() => {
    if (slotCount <= 1) return

    let intervalId: number | null = null

    const start = () => {
      if (intervalId !== null) return
      intervalId = window.setInterval(() => {
        setRawIndex(prev => (prev + 1) % slotCount)
      }, ROTATION_INTERVAL_MS)
    }
    const stop = () => {
      if (intervalId === null) return
      window.clearInterval(intervalId)
      intervalId = null
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') stop()
      else start()
    }

    if (document.visibilityState !== 'hidden') start()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [slotCount])

  const activeIndex = slotCount > 0 ? rawIndex % slotCount : 0
  return { activeIndex, reducedMotion }
}
